import { Breadcrumb, Flex, GridAlpha } from '@applyboard/crystal-ui';
import { ApplicationDetailsTabs } from '../components/ApplicationDetailsTabs/ApplicationDetailsTabs';
import { useAppProcessing } from '~/pages/ApplicationDetails/context';
import { UniqueApplicationsListProvider } from '../context/UniqueApplicationsListProvider';
import React from 'react';

import {
  ProgramDetails,
  ApplicationDetailsHeading,
  StatusAndCitizenship,
  ContactInformation,
  ApplicantInformation,
  EducationHistory,
  LanguageProficiency,
  OtherRequiredDocuments,
  ApplicationProcessing,
  CardControlActions,
  DecisionLetters,
  UploadAdditionalDocuments,
  // processing
  AdditionalDocumentNotice,
  DeferralRequestNotice,
  DocumentRequestModeNotice,
  UniqueApplicationsNotice,
  UniqueApplicationsListTable,
} from './../components';
import { AgentInformation } from '../components/Cards/AgentInformation';
import { AlternateProgramInformation } from '../components/Cards/AlternateProgramInformation';
import { ApplicationTimeline } from '../components/Cards/ApplicationTimeline';
import { BreadcrumbItem } from '~/utils';

type DesktopLayoutProps = { programDetailsLineSplit: boolean; breadcrumbItems: BreadcrumbItem[] };
export function DesktopLayout(props: DesktopLayoutProps) {
  const { application } = useAppProcessing();
  const applicationStatus =
    application.status.code === 'DECLINED' ||
    application.status.code === 'INITIAL_OFFER' ||
    application.status.code === 'WAITLISTED' ||
    application.status.code === 'ENROLMENT_CONFIRMED' ||
    application.status.code === 'FINAL_OFFER_ISSUED';
  const shouldRenderAIMLFeature =
    Boolean(application.statusAndCitizenship.documents[0]) &&
    Boolean(application.statusAndCitizenship.documents[0].documentReviewSuggestion);

  const passportId =
    application.statusAndCitizenship.passportId !== undefined
      ? application.statusAndCitizenship.passportId
      : 'NOT_AVAILABLE';

  const currentApplicationId = application.id;
  const listRef = React.useRef<HTMLDivElement>(null);

  function scrollToApplicationsTable() {
    listRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function applicationDetailsLayout() {
    return (
      <Flex gap={8} align="stretch" direction="column">
        <GridAlpha columns="repeat(3, minmax(0, 1fr))" areas={['left right']} columnGap={10}>
          <GridAlpha.Item areaName="left" columnStart={1} columnEnd={3}>
            <Flex gap={8} direction="column">
              <ApplicantInformation />
              <ContactInformation />
              {!shouldRenderAIMLFeature && <StatusAndCitizenship />}
              <EducationHistory />
              <LanguageProficiency />
              <OtherRequiredDocuments />
            </Flex>
          </GridAlpha.Item>
          <GridAlpha.Item areaName="right" columnStart={3} columnEnd={3}>
            <Flex gap={8} direction="column">
              <ApplicationTimeline />
              <AlternateProgramInformation />
              <AgentInformation />
            </Flex>
          </GridAlpha.Item>
        </GridAlpha>
        <div ref={listRef}>{passportId !== 'NOT_AVAILABLE' && <UniqueApplicationsListTable />}</div>
      </Flex>
    );
  }
  return (
    <div aria-label="desktop" style={{ marginBottom: '10px' }}>
      <UniqueApplicationsListProvider passportId={passportId} currentApplicationId={currentApplicationId}>
        <Flex pt={10} pb={2} px={8} gap={6} direction="column">
          <Breadcrumb items={props.breadcrumbItems} />
        </Flex>
        <Flex px={8} gap={6} direction="column">
          <Flex justify="between" align="center" gap={2}>
            <ApplicationDetailsHeading />
            <ApplicationProcessing />
          </Flex>
          <DeferralRequestNotice />
          <AdditionalDocumentNotice />
          <DocumentRequestModeNotice />
          <ProgramDetails lineSplit={props.programDetailsLineSplit} />
          {passportId !== 'NOT_AVAILABLE' && <UniqueApplicationsNotice onViewTableClick={scrollToApplicationsTable} />}
          <CardControlActions />
          {applicationStatus ? (
            <ApplicationDetailsTabs
              submittedApplicationDetails={applicationDetailsLayout()}
              applicationDetails={
                <Flex gap={8} align="stretch" direction="column">
                  <DecisionLetters />
                  <UploadAdditionalDocuments />
                </Flex>
              }
            />
          ) : (
            applicationDetailsLayout()
          )}
        </Flex>
      </UniqueApplicationsListProvider>
    </div>
  );
}
